import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a badge with the variant SKU.
 * Listens for variant update events and updates the badge text accordingly.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} badgeWrapper - The wrapper div for the badge
 * @property {HTMLElement} badgeText - The span element that displays the badge text
 *
 * @extends {Component<Refs>}
 */
class BadgeSkuComponent extends Component {
  requiredRefs = ['badgeWrapper', 'badgeText'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /**
   * Updates the badge with the variant SKU.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';

      if (variantSku) {
        this.style.display = 'block';
        this.refs.badgeWrapper.style.display = '';
        this.refs.badgeText.textContent = `SKU: ${variantSku}`;
      } else {
        this.style.display = 'none';
        this.refs.badgeWrapper.style.display = 'none';
        this.refs.badgeText.textContent = '';
      }
    }
  };
}

if (!customElements.get('badge-sku-component')) {
  customElements.define('badge-sku-component', BadgeSkuComponent);
}
